import { roundWithLocale } from '@sketch/utils'
import {
  Gradient,
  Point,
  GradientColorInterpolation,
  GradientType,
} from '@sketch/scene-model'

// Taken from Sketch
// https://github.com/sketch-hq/Sketch/blob/b373bfc2207956a93f23116cae2770fa296c3e9a/Modules/SketchModel/Source/MSLayer%2BCSS.m#L208-L211
const slopeBetweenPoints: (a: Point, b: Point) => number = (a, b) =>
  Math.atan2(b.y - a.y, b.x - a.x)

const radiansToDegrees = (radians: number) => radians * (180.0 / Math.PI)

const gradientAngle: (gradient: Gradient) => number = gradient => {
  const slope = slopeBetweenPoints(gradient.from, gradient.to)
  return 90 + radiansToDegrees(slope)
}

const gradientStops: (gradient: Gradient) => string = gradient => {
  const sortedStops = [...gradient.stops].sort(
    (lhs, rhs) => lhs.position - rhs.position
  )

  // An angular gradient wraps: Sketch interpolates from the last stop back to
  // the first, so the CSS needs that closing stop to match. `length` is checked
  // because a gradient can reach here with no stops at all, and spreading
  // `sortedStops[0]` would then push a stop with no colour to read.
  if (gradient.type === GradientType.Angular && sortedStops.length > 0) {
    sortedStops.push({
      ...sortedStops[0],
      position: 1,
    })
  }

  const stops = sortedStops
    .map(
      stop =>
        `rgba(${stop.color.red}, ${stop.color.green}, ${stop.color.blue}, ${
          stop.color.alpha
        }) ${roundWithLocale(stop.position * 100, 0)}%`
    )
    .join(', ')
  return stops
}

const colorInterpolationSuffix = (gradient: Gradient): string => {
  switch (gradient.colorInterpolation) {
    case GradientColorInterpolation.Oklab:
      return 'in oklab'
    case GradientColorInterpolation.Oklch:
      return 'in oklch'
    case GradientColorInterpolation.Rgb:
      return ''
  }
}

/**
 * The gradient as a CSS `background` value.
 *
 * Radial and angular gradients are approximated the way the CSS we generate for
 * the layer approximates them - a centred `radial-gradient`, and a
 * `conic-gradient` starting at 90deg. They used to fall back to `transparent`,
 * which left the gradient popover's swatch empty for every gradient that was
 * not linear.
 *
 * The one place this parts company with the CSS the Copy CSS button hands over
 * is the closing stop of an angular gradient: the preview wraps back to the
 * first stop, as the angular preview icon does, and `useGenerateLayerCSS` does
 * not.
 */
const gradientBackground: (gradient: Gradient) => string = gradient => {
  const stops = gradientStops(gradient)
  const suffix = colorInterpolationSuffix(gradient)

  switch (gradient.type) {
    case GradientType.Radial:
      return `radial-gradient(${suffix ? `${suffix}, ` : ''}${stops})`
    case GradientType.Angular:
      return `conic-gradient(from 90deg${suffix ? ` ${suffix}` : ''}, ${stops})`
    default: {
      const angle = gradientAngle(gradient)
      return `linear-gradient(${angle}deg${
        suffix ? ` ${suffix}` : ''
      }, ${stops})`
    }
  }
}

export {
  gradientBackground,
  gradientAngle,
  gradientStops,
  colorInterpolationSuffix,
}
